import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Globe2, Gauge, Bitcoin, TrendingUp, Landmark } from "lucide-react";
import { getGlobal } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, Badge, Spark, LiveBadge, Loader, ScoreBar } from "../components/Primitives";
import { ratingColor, fmtPct, POS, NEG } from "../lib/format";

function Gauge24({ value, label }) {
  const pct = Math.max(-100, Math.min(100, value));
  const up = value >= 0;
  return (
    <div>
      <div className="flex justify-between text-[11px] mb-1"><span className="text-[#64748b]">{label}</span><span className="font-mono" style={{ color: up ? POS : NEG }}>{fmtPct(value)}</span></div>
      <div className="h-1.5 rounded-full bg-[#1e293b] relative overflow-hidden">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-[#2d3c59]" />
        <div className="h-full absolute top-0" style={{ background: up ? POS : NEG, width: `${Math.abs(pct) / 2}%`, left: up ? "50%" : `${50 - Math.abs(pct) / 2}%` }} />
      </div>
    </div>
  );
}

export default function GlobalMacro() {
  const { money, usdInr, macro: ctxMacro } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["global"], queryFn: getGlobal, refetchInterval: 45000 });
  if (isLoading || !data) return <Loader />;

  const macro = data.macro || ctxMacro;
  const inst = data.instruments;
  const equities = inst.filter((x) => x.asset_class === "global_equity");
  const commodities = inst.filter((x) => x.asset_class === "gold_commodity");
  const crypto = inst.filter((x) => x.asset_class === "crypto");
  const treasuries = inst.filter((x) => x.asset_class === "sovereign_debt");
  const spread = (macro.india_gsec_10y - macro.us_treasury_10y).toFixed(2);
  const fx = [
    { p: "USD / INR", v: usdInr, d: -0.12 },
    { p: "EUR / INR", v: (usdInr * 1.08).toFixed(2), d: 0.21 },
    { p: "GBP / INR", v: (usdInr * 1.27).toFixed(2), d: 0.34 },
    { p: "JPY / INR", v: (usdInr / 149).toFixed(3), d: -0.45 },
  ];

  return (
    <div className="space-y-5">
      {/* Macro strip */}
      <Card className="p-4" testid="macro-panel">
        <div className="flex items-center gap-2 mb-3"><Landmark className="w-4 h-4 text-[#00e5ff]" /><Overline>Macro & Rate Environment</Overline><Badge color="#a855f7" className="ml-auto">{macro.regime}</Badge></div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 font-mono">
          <div><div className="overline">IN 10Y G-Sec</div><div className="text-lg text-[#f8fafc]">{macro.india_gsec_10y}%</div></div>
          <div><div className="overline">US 10Y UST</div><div className="text-lg text-[#f8fafc]">{macro.us_treasury_10y}%</div></div>
          <div><div className="overline">IN–US Spread</div><div className="text-lg text-[#00e676]">{spread}%</div></div>
          <div><div className="overline">RBI Repo</div><div className="text-lg text-[#f8fafc]">{macro.repo_rate}%</div></div>
          <div><div className="overline">Fed Funds</div><div className="text-lg text-[#f8fafc]">{macro.fed_funds}%</div></div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* US equities & ETFs */}
        <Card className="lg:col-span-2 p-5" testid="global-equities-card">
          <div className="flex items-center gap-2 mb-3"><Globe2 className="w-4 h-4 text-[#818cf8]" /><Overline>US Equities & International ETFs</Overline></div>
          <div className="scroll-x">
            <table className="w-full text-sm min-w-[520px]">
              <thead><tr className="text-left overline border-b border-[#1e293b]">
                <th className="pb-2 font-medium">Instrument</th><th className="pb-2 font-medium text-right">Price</th>
                <th className="pb-2 font-medium text-right">12M RS</th><th className="pb-2 font-medium text-right">Beta</th>
                <th className="pb-2 font-medium">Score</th><th className="pb-2 font-medium text-right">Rating</th></tr></thead>
              <tbody>
                {equities.map((x, i) => (
                  <tr key={x.id} className="border-b border-[#131a2a]" data-testid={`global-equity-row-${i}`}>
                    <td className="py-2.5"><div className="font-medium text-[#f8fafc]">{x.symbol}</div><div className="text-[11px] text-[#64748b]">{x.sector}</div></td>
                    <td className="py-2.5 text-right font-mono text-[#94a3b8]">{money(x.price_inr, { compact: true })}</td>
                    <td className="py-2.5 text-right font-mono" style={{ color: x.t.rel_strength >= 0 ? POS : NEG }}>{fmtPct(x.t.rel_strength, 1)}</td>
                    <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.t.beta}</td>
                    <td className="py-2.5"><div className="flex items-center gap-2"><span className="font-mono" style={{ color: ratingColor(x.scores.rating) }}>{x.scores.composite}</span><div className="w-14"><ScoreBar value={x.scores.composite} color={ratingColor(x.scores.rating)} /></div></div></td>
                    <td className="py-2.5 text-right"><Badge color={ratingColor(x.scores.rating)}>{x.scores.rating}</Badge></td>
                  </tr>
                ))}
                {treasuries.map((x, i) => (
                  <tr key={x.id} className="border-b border-[#131a2a]" data-testid={`global-debt-row-${i}`}>
                    <td className="py-2.5"><div className="font-medium text-[#f8fafc]">{x.symbol}</div><div className="text-[11px] text-[#64748b]">{x.sector}</div></td>
                    <td className="py-2.5 text-right font-mono text-[#94a3b8]">{money(x.price_inr, { compact: true })}</td>
                    <td className="py-2.5 text-right font-mono" style={{ color: x.t.rel_strength >= 0 ? POS : NEG }}>{fmtPct(x.t.rel_strength, 1)}</td>
                    <td className="py-2.5 text-right font-mono text-[#94a3b8]">{x.t.beta}</td>
                    <td className="py-2.5"><div className="flex items-center gap-2"><span className="font-mono" style={{ color: ratingColor(x.scores.rating) }}>{x.scores.composite}</span><div className="w-14"><ScoreBar value={x.scores.composite} color={ratingColor(x.scores.rating)} /></div></div></td>
                    <td className="py-2.5 text-right"><Badge color={ratingColor(x.scores.rating)}>{x.scores.rating}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* FX ticker */}
        <Card className="p-5" testid="fx-card">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-[#00e676]" /><Overline>Currency FX Rates</Overline></div>
          <div className="space-y-3">
            {fx.map((f) => (
              <div key={f.p} className="flex items-center justify-between py-1.5 border-b border-[#131a2a]" data-testid={`fx-${f.p}`}>
                <span className="font-mono text-sm text-[#94a3b8]">{f.p}</span>
                <div className="text-right">
                  <div className="font-mono text-[#f8fafc]">{f.v}</div>
                  <div className="font-mono text-[11px]" style={{ color: f.d >= 0 ? POS : NEG }}>{fmtPct(f.d)}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4"><Overline className="mb-2">Commodities · 24h / 90d</Overline>
            <div className="space-y-3">
              {commodities.map((x) => (
                <div key={x.id} className="rounded-lg border border-[#1e293b] p-3" data-testid={`commodity-${x.id}`}>
                  <div className="flex items-center justify-between mb-2"><span className="font-medium text-sm text-[#f8fafc]">{x.sector}</span><Badge color={ratingColor(x.scores.rating)}>{x.scores.composite}</Badge></div>
                  <Gauge24 value={x.t.macd_slope * 2} label="24h momentum" />
                  <div className="h-1.5" />
                  <Gauge24 value={x.t.rel_strength} label="90d momentum" />
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Crypto monitor */}
      <div>
        <div className="flex items-center gap-2 mb-3"><Bitcoin className="w-4 h-4 text-[#f59e0b]" /><Overline>Digital Asset Monitor · Layer-1</Overline></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {crypto.map((x, i) => {
            const c = x.crypto || {};
            return (
              <motion.div key={x.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Card className="p-5" testid={`crypto-card-${x.id}`} glow={x.scores.rating === "Strong Buy"}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2"><span className="font-semibold text-[#f8fafc]">{x.name}</span><LiveBadge live={x.live} /></div>
                      <div className="font-mono text-2xl font-semibold mt-1">{money(x.price_inr, { compact: true })}</div>
                      <div className="font-mono text-xs mt-0.5" style={{ color: c.change_24h >= 0 ? POS : NEG }}>{fmtPct(c.change_24h)} · 24h</div>
                    </div>
                    <Badge color={ratingColor(x.scores.rating)}>{x.scores.rating}</Badge>
                  </div>
                  <div className="my-3"><Spark data={c.sparkline} w={240} h={40} /></div>
                  <div className="grid grid-cols-3 gap-2 font-mono text-center">
                    {[["24h", c.change_24h], ["7d", c.change_7d], ["90d", c.change_90d]].map(([k, v]) => (
                      <div key={k} className="rounded-md border border-[#1e293b] py-2">
                        <div className="overline">{k}</div>
                        <div className="text-sm" style={{ color: v >= 0 ? POS : NEG }}>{fmtPct(v, 1)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 font-mono text-[11px] text-[#64748b]">
                    <span>Composite <span style={{ color: ratingColor(x.scores.rating) }}>{x.scores.composite}</span></span>
                    <span>β {x.t.beta} · corr&lt;Nifty {(x.t.beta * 0.22).toFixed(2)}</span>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
